'use client';
import { useState } from 'react';
import { IRepository, IRepositoryEntry } from '@/types';
import Button from '@/ui/Button';
import { useRouter } from 'next/navigation';
import { openMarimoSandboxAction } from './openMarimoSandboxAction';
import { ReaderIcon, CaretDownIcon } from '@radix-ui/react-icons';
import { IModalResource } from '@/lib/modalResourcesApi';
import { IMarimoSandboxConfig } from '@/lib/marimoSandboxesApi';
import MarimoSandboxMenu from './MarimoSandboxMenu';
import DropdownMenu from '@/ui/DropdownMenu';
import useNotebookBaseImages from '@/hooks/useNotebookBaseImages';
import useNotebookConfig from '@/hooks/useNotebookConfig';
import useOxenSession from '@/hooks/useOxenSession';
interface OpenMarimoSandboxButtonProps {
  repository: IRepository;
  entry: IRepositoryEntry;
  modalResources: IModalResource[];
  isLoggedinUser?: boolean;
}

const OpenMarimoSandboxButton: React.FC<OpenMarimoSandboxButtonProps> = ({
  repository,
  entry,
  modalResources,
  isLoggedinUser,
}) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isNotEnoughCredits, setIsNotEnoughCredits] = useState(false);

  const { baseImages, error } = useNotebookBaseImages({});
  const { notebookConfig: notebookConfiguration, error: notebookConfigError } = useNotebookConfig({
    repository,
    resource: entry.resource,
  });

  const handleOpenMarimoSandbox = async ({
    gpuModel,
    cpuCores,
    memory,
    runtime,
    notebookImageId,
    notebookBaseImageId,
    buildScript,
  }: {
    gpuModel: string;
    cpuCores: number;
    memory: number;
    runtime: number;
    notebookBaseImageId: string;
    notebookImageId?: string;
    buildScript?: string;
  }) => {
    setIsLoading(true);
    try {
      const result = await openMarimoSandboxAction({
        repository,
        resource: entry.resource!,
        config: {
          gpu_model: gpuModel || undefined,
          cpu_cores: cpuCores,
          memory_mb: memory * 1024,
          timeout_secs: runtime * 3600,
          notebook_image_id: notebookImageId || undefined,
          notebook_base_image_id: notebookBaseImageId,
          build_script: buildScript,
        },
      });

      if (result.status === 'success') {
        setIsLoading(false);
        setIsOpen(false);
        router.push(result.redirectUrl);
      } else if (result.status === 'failure' && result.message.includes('Not enough credits')) {
        setIsLoading(false);
        setIsNotEnoughCredits(true);
      } else {
        setIsLoading(false);
        setIsOpen(false);
        console.error('Failed to create Marimo sandbox:', result.message);
      }
    } catch (error) {
      console.error('Error creating Marimo sandbox:', error);
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  if (entry.extension !== 'py') {
    return null;
  }

  const { authorization } = useOxenSession();
  const hasAccess = authorization.forRepository(repository).canCreateNotebooks();

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <Button.Success disabled={!hasAccess} small style={{ whiteSpace: 'nowrap' }}>
          <ReaderIcon />
          Launch Notebook
          <CaretDownIcon />
        </Button.Success>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <MarimoSandboxMenu
          modalResources={modalResources}
          isLoading={isLoading}
          onSubmit={handleOpenMarimoSandbox}
          hasAccess={hasAccess}
          config={notebookConfiguration}
          baseImageList={baseImages || []}
          isLoggedinUser={isLoggedinUser}
          isNotEnoughCredits={isNotEnoughCredits}
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default OpenMarimoSandboxButton;
